import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Modal, IconButton, useTheme, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { baseUrl } from "../../util/constant";

const PreviewPicture = ({ open, label, handleClose, file }) => {
  const theme = useTheme();
  const imageFormats = [".jpg", ".jpeg", ".png", ".gif"];
  const url = baseUrl;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    setSelectedIndex(0);
  }, [file]);

  const selected = file[selectedIndex];

  const isImage = (file) => {
    if (!file?.extension) return false;
    const extension = file.extension.toLowerCase();
    return imageFormats.some((format) => extension.includes(format));
  };

  const handleThumbnailClick = (index) => setSelectedIndex(index);

  const handleNext = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % file.length);
  }, [file.length]);

  const handlePrev = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + file.length) % file.length);
  }, [file.length]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "ArrowLeft") handlePrev();
      else if (event.key === "ArrowRight") handleNext();
    };
    if (open) window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handlePrev, handleNext, open]);

  // تحميل جميع الصور كملف ZIP
  const handleDownloadAllZip = async () => {
    const zip = new JSZip();
    const folder = zip.folder("images");
    await Promise.all(
      file.map(async (item) => {
        const response = await fetch(`${url}${item.filePath}`);
        const blob = await response.blob();
        folder.file(item.fileName, blob);
      })
    );
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "all_images.zip");
  };

  if (!selected) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="content"
        sx={{
          width: { xs: "98vw", md: "80vw" },
          maxHeight: "90vh",
          bgcolor: theme.palette.background.paper,
          borderRadius: 3,
          p: { xs: 1, md: 4 },
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          outline: "none",
          mx: "auto",
          my: "5vh",
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            {label}
          </Typography>
          <Box>
            {/* زر تحميل جميع الصور كملف ZIP */}
            <IconButton
              onClick={handleDownloadAllZip}
              sx={{ mr: 1, bgcolor: theme.palette.background.paper }}
              title="Download All Images as ZIP"
            >
              <FileDownloadIcon sx={{ color: theme.palette.secondary.main }} />
            </IconButton>
            {/* زر إغلاق */}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Large Image Display */}
        <Box sx={{ display: "flex", justifyContent: "center", alignContent: "center", m: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
            <IconButton onClick={handlePrev}>
              <NavigateBeforeIcon />
            </IconButton>
            <Box position="relative">
              {/* عداد الصور */}
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 16,
                  bgcolor: theme.palette.background.paper,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  boxShadow: "0 1px 6px 0 rgba(11,60,93,0.08)"
                }}
              >
                {selectedIndex + 1} / {file.length}
              </Typography>
              {/* زر تحميل الصورة الحالية */}
              <IconButton
                component="a"
                href={`${url}${selected.filePath}`}
                download={selected.fileName}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ position: "absolute", top: 8, left: 8, bgcolor: theme.palette.background.paper }}
              >
                <DownloadIcon sx={{ color: theme.palette.secondary.main }} />
              </IconButton>
              {/* الصورة */}
              {isImage(selected) ? (
                <a
                  href="#"
                  onClick={e => { e.preventDefault(); setZoomed(true); }}
                  style={{ display: "block" }}
                >
                  <img
                    src={`${url}${selected.filePath}`}
                    alt={selected.fileName}
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: "370px",
                      objectFit: "contain",
                      background: theme.palette.background.default,
                      cursor: "zoom-in"
                    }}
                  />
                </a>
              ) : (
                <a
                  href={`${url}${selected.filePath}`}
                  download={selected.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Box
                    sx={{
                      width: "170px",
                      height: "170px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: theme.palette.grey[100],
                    }}
                  >
                    <Typography variant="h3" color={theme.palette.primary.main}>
                      {selected.extension?.substring(1).toUpperCase()}
                    </Typography>
                  </Box>
                </a>
              )}
              <Typography variant="body1" sx={{ textAlign: "center", fontWeight: 700, color: theme.palette.text.primary }}>
                {selected.originalFileName}
              </Typography>
            </Box>
            <IconButton onClick={handleNext}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Zoom Modal */}
        <Modal open={zoomed} onClose={() => setZoomed(false)}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              bgcolor: "rgba(0,0,0,0.85)",
              zIndex: 2000,
            }}
          >
            <img
              src={`${url}${selected.filePath}`}
              alt={selected.fileName}
              style={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: 8,
                boxShadow: "0 8px 32px 0 rgba(0,0,0,0.25)",
                background: theme.palette.background.paper,
                cursor: "zoom-out"
              }}
              onClick={() => setZoomed(false)}
            />
          </Box>
        </Modal>

        {/* Thumbnails */}
        <Box sx={{ flex: 1, overflow: "auto", mt: 3 }}>
          <Box
            display="flex"
            alignContent="center"
            justifyContent="center"
            gap={2}
            sx={{ flexWrap: "wrap" }}
          >
            {file &&
              file.length > 0 &&
              file.map((item, index) => (
                <Tooltip title={item.originalFileName} key={item.id}>
                  <Box
                    width={70}
                    height={70}
                    position="relative"
                    onClick={() => handleThumbnailClick(index)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: selectedIndex === index
                        ? `3px solid ${theme.palette.secondary.main}`
                        : `2px solid ${theme.palette.grey[300]}`,
                      boxShadow: selectedIndex === index
                        ? `0 0 12px 0 ${theme.palette.secondary.main}33`
                        : "0 1px 6px 0 rgba(11,60,93,0.08)",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: theme.palette.secondary.main,
                        boxShadow: `0 0 12px 0 ${theme.palette.secondary.main}33`,
                      },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: theme.palette.background.paper,
                    }}
                  >
                    {isImage(item) ? (
                      <img
                        src={`${url}${item.filePath}`}
                        alt={item.fileName}
                        width="100%"
                        height="100%"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: theme.palette.grey[100],
                        }}
                      >
                        <Typography variant="h5" color={theme.palette.primary.main}>
                          {item.extension?.substring(1).toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PreviewPicture;
